import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  IconButton, MenuItem, Select, SelectChangeEvent, Typography,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import { createStyles, makeStyles } from '@mui/styles';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { ImportPage } from 'services/imports/fileToPages';

const useStyles = makeStyles((theme: Theme) => createStyles({
  root: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    minWidth: 0,
  },
  control: {
    flexShrink: 0,
    minWidth: theme.spacing(5.5),
    minHeight: theme.spacing(5.5),
  },
  icon: {
    fontSize: theme.typography.body2.fontSize,
  },
  counter: {
    flexShrink: 0,
    whiteSpace: 'nowrap',
    color: theme.palette.text.secondary,
  },
  select: {
    flex: '1 1 auto',
    minWidth: 0,
    maxWidth: theme.spacing(32),
    minHeight: theme.spacing(5.5),
  },
  value: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  fileName: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  pageNumber: {
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
}));

type SourcePageNavProps = {
  pages: readonly ImportPage[];
  current: ImportPage;
  onChange: (pageId: string) => void;
};

/** Previous / next buttons around a position counter, plus a select whose value keeps the page number visible. */
export const SourcePageNav = ({ pages, current, onChange }: SourcePageNavProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const index = pages.findIndex((page) => page.id === current.id);
  const fullName: string = t('imports:pages.thumbnailAlt', { file: current.fileName, page: current.pageNumber });
  const selectLabel = `${t('imports:viewer.selectPage')}: ${fullName}`;

  const goTo = (offset: number) => {
    const target = pages[index + offset];
    if (target) onChange(target.id);
  };

  const renderCurrent = () => (
    <span className={classes.value} title={fullName}>
      <span className={classes.fileName}>{current.fileName}</span>
      <span className={classes.pageNumber}>{t('imports:viewer.page', { page: current.pageNumber })}</span>
    </span>
  );

  return (
    <div className={classes.root}>
      <IconButton
        aria-label={t('imports:viewer.previousPage')}
        disabled={index <= 0}
        onClick={() => goTo(-1)}
        className={classes.control}
        size="large"
      >
        <FontAwesomeIcon icon={faChevronLeft} className={classes.icon} aria-hidden="true" />
      </IconButton>
      <Typography variant="body2" component="span" className={classes.counter}>
        {t('imports:viewer.pageOf', { page: index + 1, total: pages.length })}
      </Typography>
      <IconButton
        aria-label={t('imports:viewer.nextPage')}
        disabled={index < 0 || index >= pages.length - 1}
        onClick={() => goTo(1)}
        className={classes.control}
        size="large"
      >
        <FontAwesomeIcon icon={faChevronRight} className={classes.icon} aria-hidden="true" />
      </IconButton>
      <Select
        value={current.id}
        variant="outlined"
        margin="dense"
        renderValue={renderCurrent}
        onChange={(event: SelectChangeEvent<string>) => onChange(String(event.target.value))}
        inputProps={{ 'aria-label': selectLabel }}
        className={classes.select}
      >
        {pages.map((page) => (
          <MenuItem key={page.id} value={page.id}>
            {t('imports:pages.thumbnailAlt', { file: page.fileName, page: page.pageNumber })}
          </MenuItem>
        ))}
      </Select>
    </div>
  );
};
